import { DropdownMenu } from "@/components/molecules/DropdownMenu/DropdownMenu";
import { parseHexWithAlpha } from "@/layers/utility/color";
import { IntegerInput } from "@/overlay/components/CommonParts/CustomInput";
import "@/overlay/components/CommonParts/Threshold/CompleteThreshold.scss";
import {
  GenericColorPicker,
  type GenericColorPickerSavedColorsState,
} from "@/overlay/components/GenericColorPicker/GenericColorPicker";
import { SavedColorsCloseConfirmationModal } from "@/overlay/components/GenericColorPicker/SavedColorsCloseConfirmationModal";
import {
  type SavedColorsCloseConfirmationConfig,
  requestSavedColorsClose,
} from "@/overlay/components/GenericColorPicker/requestSavedColorsClose";
import { FormField } from "@/overlay/components/form-field";
import { DefaultPlotThreshold, type PlotThreshold } from "@/reducer/client-models";
import areaSvg from "@/style/img/area_icon.svg";
import lineSvg from "@/style/img/line_icon.svg";
import splineSvg from "@/style/img/spline_icon.svg";
import { ConditionOptions, conditionValues } from "@/utility/conditionalExpressions";
import { classNames } from "@/utility/jsx";
import { t } from "@lingui/core/macro";
import { useRef, useState } from "react";
import { useColor } from "react-color-palette";
import { type DraggableItemIdentifier, SortableList } from "../../CommonParts/SortableList";
import { Button, ButtonBar } from "../../CommonParts/buttons";
import style from "./style.module.scss";

const IconToggleButton = ({
  icon,
  isActive,
  onClick,
  ariaLabel,
  disabled,
}: {
  icon: string;
  isActive: boolean;
  onClick?: () => void;
  ariaLabel?: string;
  disabled?: boolean;
}) => (
  <button
    className={classNames(style["square-button"], isActive && style["square-button--highlighted"])}
    onClick={onClick}
    type="button"
    aria-label={ariaLabel}
    disabled={disabled}
  >
    <img src={icon} alt={ariaLabel || "Toggle icon"} />
  </button>
);

const ColorButton = ({ value, onClick }: { value: string; onClick?: () => void }) => (
  <button className={style["square-button"]} onClick={onClick} type="button">
    <div className={style["square-button__color"]} style={{ background: value }} />
  </button>
);

const ThresholdOption = ({
  threshold,
  onRemove,
  onThresholdChange,
  step,
  allowDecimals,
}: {
  threshold: PlotThreshold;
  onRemove: () => void;
  onThresholdChange: (data: Partial<PlotThreshold>) => void;
  step?: number;
  allowDecimals?: boolean;
}) => {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [savedColorsState, setSavedColorsState] = useState<GenericColorPickerSavedColorsState | null>(null);
  const [closeConfirmation, setCloseConfirmation] = useState<SavedColorsCloseConfirmationConfig | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [color, setColor] = useColor(threshold.color);

  const handleClose = () => {
    requestSavedColorsClose({
      savedColorsState,
      onConfirmedClose: () => {
        onThresholdChange({ color: parseHexWithAlpha(color.hex) });
        setCloseConfirmation(null);
        setShowColorPicker(false);
      },
      confirmUnsavedChanges: (config) => {
        setCloseConfirmation(config);
      },
    });
  };

  const toggleColorPicker = () => setShowColorPicker((prev) => !prev);

  return (
    <div ref={wrapperRef}>
      <div className={style["threshold-options"]}>
        <span className={classNames("material-icons", style["drag-icon"])}>drag_indicator</span>
        <div className={style["threshold-buttons"]}>
          <span ref={anchorRef}>
            <ColorButton onClick={toggleColorPicker} value={color.hex} />
          </span>
          <DropdownMenu
            isOpen={showColorPicker}
            withinPortal={true}
            onClose={() => handleClose()}
            anchorRef={anchorRef}
          >
            {closeConfirmation ? (
              <SavedColorsCloseConfirmationModal isOpen={true} {...closeConfirmation} />
            ) : (
              <GenericColorPicker
                color={color}
                onColorChange={setColor}
                onSavedColorsStateChange={setSavedColorsState}
              />
            )}
          </DropdownMenu>
          <mm-tooltip-wrapper position="top" tooltip-text={t`Threshold on data`}>
            <IconToggleButton
              icon={splineSvg}
              isActive={threshold.dataThresholdShow}
              onClick={() => onThresholdChange({ dataThresholdShow: !threshold.dataThresholdShow })}
              ariaLabel="Spline"
            />
          </mm-tooltip-wrapper>
          <mm-tooltip-wrapper position="top" tooltip-text={t`Threshold Line`}>
            <IconToggleButton
              icon={lineSvg}
              isActive={threshold.lineThresholdShow}
              onClick={() => onThresholdChange({ lineThresholdShow: !threshold.lineThresholdShow })}
              ariaLabel="Line"
            />
          </mm-tooltip-wrapper>
          <mm-tooltip-wrapper position="top" tooltip-text={t`Threshold Area`}>
            <IconToggleButton
              icon={areaSvg}
              isActive={threshold.areaThresholdShow}
              onClick={() => onThresholdChange({ areaThresholdShow: !threshold.areaThresholdShow })}
              ariaLabel="Area"
              disabled={threshold.condition === "=="}
            />
          </mm-tooltip-wrapper>
        </div>
        <div className={style["threshold-inputs"]}>
          <select
            value={threshold.condition}
            onChange={(e) => {
              const selectedCondition = e.target.value as ConditionOptions;
              const update: Partial<PlotThreshold> = { condition: selectedCondition };

              if (selectedCondition === "==") {
                update.areaThresholdShow = false;
              }

              onThresholdChange(update);
            }}
            data-testid="condition-select"
          >
            {conditionValues.map((condition) => (
              <option key={condition} value={condition}>
                {ConditionOptions[condition]}
              </option>
            ))}
          </select>
          <IntegerInput
            value={threshold.value}
            onChange={(value) => onThresholdChange({ value })}
            step={step}
            decimals={allowDecimals || !Number.isInteger(step)}
            data-testid="threshold-value"
          />
          <span className="material-icons" onClick={onRemove}>
            close
          </span>
        </div>
      </div>
    </div>
  );
};

const ThresholdPanel = ({
  thresholds,
  onMoveIndex,
  onThresholdAdd,
  onThresholdChange,
  onThresholdRemove,
}: {
  thresholds: PlotThreshold[];
  onMoveIndex: (dragStart: DraggableItemIdentifier, dropTarget: DraggableItemIdentifier) => void;
  onThresholdAdd: (threshold: PlotThreshold) => void;
  onThresholdChange: (index: number, props: Partial<PlotThreshold>) => void;
  onThresholdRemove: (index: number) => void;
}) => (
  <div className={style.threshold}>
    <FormField label={t`thresholds`} />
    <SortableList
      className="box-section--breakout"
      onSwapItems={onMoveIndex}
      items={thresholds.map((threshold) => ({
        id: threshold.index,
        content: (
          <ThresholdOption
            key={`${threshold.index}`}
            threshold={threshold}
            onThresholdChange={(props) => onThresholdChange(threshold.index, props)}
            onRemove={() => onThresholdRemove(threshold.index)}
          />
        ),
      }))}
    />
    <ButtonBar align="right" className="layer-stack-thresholds">
      <Button onClick={() => onThresholdAdd({ ...DefaultPlotThreshold, index: thresholds.length })}>
        {t`Add Threshold`}
        <span className="material-icons">add</span>
      </Button>
    </ButtonBar>
  </div>
);

export { ThresholdPanel };
